import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Save, Search, Users, AlertTriangle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Batch, Student, BatchFormData } from '../../types/batch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Card, CardContent, CardDescription, CardFooter,
  CardHeader, CardTitle,
} from '../../components/ui/card';
import {
  Form, FormControl, FormDescription, FormField, FormItem,
  FormLabel, FormMessage,
} from '../../components/ui/form';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useForm } from 'react-hook-form';
import StudentSelectCard from '../../components/batches/StudentSelectCard';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../../components/ui/tabs';
import {
  Alert, AlertDescription, AlertTitle,
} from '../../components/ui/alert';
import { collection, getDocs, doc, getFirestore, getDoc, updateDoc, setDoc, onSnapshot, query, where } from 'firebase/firestore'; // Firestore methods  
import { app } from '../../firebase'; // Your Firebase app  
import { AnyNsRecord } from 'dns';

const emptyBatchForm: any = {
  id: '',
  batchName: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split('T')[0],
  status: '',
  students: [],
  courseId: ''
};

const AddOrEditBatch: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditMode = !!batchId;

  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const db = getFirestore(app); // Initialize Firestore  
  const [courseAttandance, setCourseAttandance] = useState({});

  const form = useForm<any>({
    defaultValues: emptyBatchForm
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the batch data  
        let batchData: any = {};
        if (isEditMode && !!batchId) {
          const batchRef = doc(db, 'courseBatches', batchId);
          const batchSnapshot = await getDoc(batchRef);
          if (batchSnapshot.exists()) {
            batchData = { id: batchSnapshot.id, ...batchSnapshot.data() } as any;
            console.log(batchData)
            form.reset({
              id: batchData?.id,
              batchName: batchData?.batchName || '',  // Use empty string as fallback  
              description: batchData?.description || '',
              startDate: batchData?.startDate || '',
              endDate: batchData?.endDate || '',
              status: batchData?.status || '',
              students: batchData?.students || [], // Fill with student emails  
              courseId: batchData?.courseId || ''
            });
            fetchCourseAttandance(batchData);
            setSelectedStudents(batchData.students);
          } else {
            toast({
              title: "Batch not found",
              description: "The requested batch could not be found.",
              variant: "destructive"
            });
            navigate('/batches');
          }
        }

        // Fetch all students  
        const studentsCollection = collection(db, 'students');
        const studentSnapshot = await getDocs(studentsCollection);
        const allStudents = studentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as any[];

        // Filter selected students based on email  
        const filteredStudents = allStudents?.filter((student: any) =>
          batchData && batchData?.students?.includes(student?.emailId) && student?.isInternal==true
        );

        const allSorted = [...filteredStudents, ...allStudents?.filter((student: any) => !batchData?.students?.includes(student?.emailId))];
        setAvailableStudents(allSorted); // Set all students for display  
        setSelectedStudents(filteredStudents); // Set the filtered students based on batch  
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Error fetching batch or students",
          description: "There was an issue loading the data.",
          variant: "destructive"
        });
      }
    };
    const fetchCourseAttandance = (batchData: any) => {
      if (!!batchId) getCourseAttendance(batchId, batchData);
    }
    const unsubscribeCourses = onSnapshot(
      collection(db, "courses"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCourses(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    fetchData();
    return () => {
      unsubscribeCourses();
    }
  }, [batchId, isEditMode, navigate, toast, form]);


  async function getCourseAttendance(batchId: any, batchData: any) {
    try {
      // Step 1: Query the course attendance collection by courseId
      const attendanceQuery = query(
        collection(db, "courseAttandance"), // Adjust your collection name as needed
        where("batchId", "==", batchId) // Query directly by courseId
      );

      const attendanceSnapshot = await getDocs(attendanceQuery);

      if (attendanceSnapshot.empty) {
        console.log("No attendance records found for this course.");
        console.log("Came Here , course : batchId", batchId);
        createNewCourseAttandance(batchData);
        // Return an empty array if no records found
      } 

      // Step 2: Extract attendance data
      const attendanceData = attendanceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(attendanceData)
      setCourseAttandance(attendanceData[0]);
      return attendanceData; // Return the retrieved attendance data

    } catch (error) {
      console.error("Error fetching course attendance: ", error);
      return null; // Return null in case of error
    }
  }


  const createNewCourseAttandance = async (batch: any) => {
    const body = {
      batchId: batchId,
      batchName: batch?.batchName,
      courseId: batch?.courseId,
      [batch.batchName]: []
    };

    try {

      const courseATtandanceRef = doc(collection(db, "courseAttandance"));

      // Add the generated ID to the course data object  
      const courseATtandanceRefWithId = {
        id: courseATtandanceRef.id, // Assign the generated ID  
        ...body
      };

      // Save the course data to Firestore with the generated ID  
      await setDoc(courseATtandanceRef, courseATtandanceRefWithId);

      toast({
        title: "Course Attandance created",
        description: `CourseAttandance for ${courseATtandanceRefWithId?.batchName} has been successfully created`,
      });
    }
    catch (error) {
      console.error('Error creating CourseAttandance:', error);
      toast({
        title: "Error",
        description: "There was an issue creating the courseAttandance.",
        variant: "destructive"
      });
    }
  }


  const filteredStudents = availableStudents.filter((student: any) =>
    student.studentsName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    student.emailId?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const toggleStudentSelection = (student: any) => {
    const isAlreadySelected = selectedStudents.some(s => s.emailId === student.emailId);

    if (isAlreadySelected) {
      setSelectedStudents(prev => prev.filter(s => s.emailId !== student.emailId));
      removeStudentBatch(student);
    } else {
      updateStudentBatch(student);
      setSelectedStudents(prev => [...prev, student]);
    }
  };

  const updateStudentBatch = async (student) => {
    try {
      const batchIds = Array.from(new Set(student?.batchIds)) ?? [batchId];
      batchIds.push(batchId);
      await setDoc(doc(db, 'students', student?.id), { ...student, batchIds: Array.from(new Set(batchIds)) });
      // toast({
      //   title: "Student Updated",
      //   description: "Student has been updated registered",
      // });
      // navigate("/students");
    }
    catch (error) {
      console.error('Error updating batch:', error);
      toast({
        title: "Error",
        description: "There was an issue updating the batch.",
        variant: "destructive"
      });
    }
  }

  const removeStudentBatch = async (student) => {
    try {
      let batchIds = Array.from(new Set(student?.batchIds)) ?? [];
      batchIds = batchIds.filter(x => x != batchId);
      await setDoc(doc(db, 'students', student?.id), { ...student, batchIds: Array.from(new Set(batchIds)) });
      // toast({
      //   title: "Student Updated",
      //   description: "Student has been updated registered",
      // });
      // navigate("/students");
    }
    catch (error) {
      console.error('Error updating batch:', error);
      toast({
        title: "Error",
        description: "There was an issue updating the batch.",
        variant: "destructive"
      });
    }
  }

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    data.students = selectedStudents.map((s: any) => s.emailId); // Map students to their email for submission  

    try {
      if (batchId) {
        // Get a reference to the specific batch document  
        const batchRef = doc(db, 'courseBatches', batchId!); // Use batchId as reference  
        console.log(data)
        const body ={ 
          batchName: data.batchName,
          id: data?.id ?? batchId,
          description: data.description,
          startDate: data.startDate,
          endDate: data.endDate,
          status: data.status,
          students: data.students, // update to the list of updated students  
          isActive: data?.status == "active" ? true : false,
          courseId: data?.courseId ?? ""
        }
        console.log(body);
        // Update the document  
        await updateDoc(batchRef,body);


        toast({
          title: isEditMode ? "Batch updated" : "Batch created",
          description: `The batch has been successfully ${isEditMode ? 'updated' : 'created'}.`
        });
        navigate(`/batches`);
      } else {
        const batchRef = doc(collection(db, "courseBatches"));

        // Add the generated ID to the course data object  
        const batchDataWithId = {
          id: batchRef.id, // Assign the generated ID  
          batchName: data.batchName,
          description: data.description,
          startDate: data.startDate,
          endDate: data.endDate,
          status: data.status,
          isActive: data?.status == "active" ? true : false,
          students: data.students, // update to the list of updated students  
          courseId: data?.courseId ?? ''
        };

        // Save the course data to Firestore with the generated ID  
        await setDoc(batchRef, batchDataWithId);

        toast({
          title: "Batch created",
          description: `${batchDataWithId.batchName} has been successfully created`,
        });
        navigate(`/batches/edit/${batchDataWithId?.id}`); // Navigate to the batches page  
      }

    } catch (error) {
      console.error('Error updating batch:', error);
      toast({
        title: "Error",
        description: "There was an issue updating the batch.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false); // Reset submitting state after operation  
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto bg-gray-50">
      <div className="mb-6">
        <Link to="/batches" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Batches
        </Link>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? 'Edit Batch' : 'Create Batch'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditMode
              ? 'Update batch details and manage students'
              : 'Create a new batch and add students'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="mb-6 grid w-full grid-cols-2">
          <TabsTrigger value="details">Batch Details</TabsTrigger>
          <TabsTrigger value="students" disabled={!batchId}>
            Students
            {!batchId && (
              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                Save First
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TabsContent value="details">
              <Card>
                <CardHeader>
                  <CardTitle>Batch Information</CardTitle>
                  <CardDescription>
                    Enter the details for this training batch
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="batchName"
                      rules={{ required: "Batch name is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Batch Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Frontend Masters 2023" {...field} />
                          </FormControl>
                          <FormDescription>
                            Give your batch a descriptive name
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="courseId"
                      rules={{ required: "Course selection is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Course</FormLabel>
                          <FormControl>
                            <Select {...field} onValueChange={field.onChange}
                              defaultValue={field.value ?? ''}>
                              <SelectTrigger>
                                <SelectValue placeholder="Choose a course" />
                              </SelectTrigger>
                              <SelectContent>
                                {
                                  courses?.map((course: any) => {
                                    return <SelectItem value={course?.id}>{course?.courseName}</SelectItem>
                                  })
                                }
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormDescription>
                            Please select the course associated with this batch
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="description"
                    rules={{ required: "Description is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Brief description about this batch"
                            {...field}
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Provide details about the focus areas of this batch
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="startDate"
                      rules={{ required: "Start date is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                              <Input
                                type="date"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endDate"
                      rules={{
                        required: "End date is required",
                        validate: value => {
                          const startDate = new Date(form.getValues('startDate'));
                          const endDate = new Date(value);
                          return endDate > startDate || "End date must be after start date";
                        }
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                              <Input
                                type="date"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="status"
                    rules={{ required: "Status is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                         {...field} onValueChange={field.onChange}
                         defaultValue={field.value ?? ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select batch status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Set the current status of this batch
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline" type="button" onClick={() => navigate('/batches')}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting} className="bg-bytesplash-blue hover:bg-bytesplash-light-blue">
                    {isSubmitting ? 'Saving...' : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditMode ? 'Update Batch' : 'Create Batch'}
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>

            <TabsContent value="students">
              <Card>
                <CardHeader>
                  <CardTitle>Manage Students</CardTitle>
                  <CardDescription>
                    Add or remove students from this batch
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search students by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Users className="mr-2 h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">
                        Selected Students: {selectedStudents.length}
                      </span>
                    </div>
                    {selectedStudents.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedStudents([])} // Clear all selections  
                      >
                        Clear All
                      </Button>
                    )}
                  </div>

                  {(searchQuery && filteredStudents.length === 0) && (
                    <Alert className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>No results found</AlertTitle>
                      <AlertDescription>
                        No students match your search query. Try a different search term.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 max-h-[400px] overflow-y-auto pr-2">
                    {filteredStudents.map(student => (
                      <StudentSelectCard
                        key={student.id}
                        student={student}
                        isSelected={selectedStudents.some(s => s.emailId === student.emailId)}
                        onToggle={toggleStudentSelection}
                      />
                    ))}
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline" type="button" onClick={() => navigate('/batches')}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting} className="bg-bytesplash-blue hover:bg-bytesplash-light-blue">
                    {isSubmitting ? 'Saving...' : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditMode ? 'Update Batch' : 'Create Batch'}
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
          </form>
        </Form>
      </Tabs>
    </div>
  );
};

export default AddOrEditBatch;